import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Category, Menu, getAllCatagory } from "../../../api/category.api";
import { getMenuStock, MenuStock } from "../../../api/menu.api";
import { getAllHistoryUser } from "../../../api/order.api";
import { loadSettingsCached } from "../../../api/settings.api";
import { State } from "../../../reducer";
import {
  isChannelOpen,
  isMenuAvailableNow,
  nextOpenFor,
  scheduleRows,
  summarizeMenuAvailability,
} from "../../../utils/schedule";
import { ikThumb } from "../../../utils/image";
import { canBypassClosure } from "../../../utils/access";

// Bangkok-local "YYYY-MM-DD" — the cafe runs on Asia/Bangkok hours, so we
// compare against that, not the browser's local timezone.
const bangkokDateKey = (d: Date | string): string =>
  new Date(d).toLocaleDateString("en-CA", { timeZone: "Asia/Bangkok" });

const MenuDessertList: React.FC = () => {
  const user = useSelector((state: State) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<Category[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());
  // menu_ids of desserts the user already ordered today (excludes cancel /
  // rejected — those didn't count). Used to disable repeat orders.
  const [orderedDessertIds, setOrderedDessertIds] = useState<Set<number>>(new Set());
  // Daily stock cap per menu (e.g. Honey toast = 20/day across all users).
  const [stockByMenu, setStockByMenu] = useState<Record<number, MenuStock>>({});
  // Global "total desserts per day per user" cap from app_settings.
  const [dessertLimit, setDessertLimit] = useState<number>(2);

  // Tick every minute so the open/closed banner stays accurate without a refresh.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const open = isChannelOpen("dessert", now);
  const bypass = canBypassClosure(user?.role);
  const next = !open ? nextOpenFor("dessert", now) : null;
  const rows = useMemo(() => scheduleRows("dessert"), []);

  useEffect(() => {
    getAllCatagory().then((all) => {
      const filtered = all.filter(
        (c) => c.categoryFor === "dessert" && c.isAvailable === "available",
      );
      setCategories(filtered);
      if (filtered.length > 0) setSelected(filtered[0].id);
      setIsLoading(false);
    });
    // Fetch today's dessert orders once on mount so we can grey out items the
    // user has already ordered. Cancelled / rejected attempts still count
    // toward the 1-per-dessert-per-day rule, so we DON'T filter them out.
    getAllHistoryUser()
      .then((orders) => {
        const today = bangkokDateKey(new Date());
        const ids = orders
          .filter(
            (o) =>
              o.menu?.category?.categoryFor === "dessert" &&
              bangkokDateKey(o.orderDate) === today,
          )
          .map((o) => o.menu.id);
        setOrderedDessertIds(new Set(ids));
      })
      .catch(() => {});
    // Fetch the global daily-stock status for limited menus (toast = 20/day).
    getMenuStock()
      .then((rows) => {
        const map: Record<number, MenuStock> = {};
        for (const r of rows) map[r.menuId] = r;
        setStockByMenu(map);
      })
      .catch(() => {});
    loadSettingsCached()
      .then((s) => setDessertLimit(s.dailyDessertLimit))
      .catch(() => {});
  }, []);

  const menus = useMemo<Menu[]>(() => {
    if (selected === null) return [];
    const c = categories.find((e) => e.id === selected);
    if (!c) return [];
    return c.menus.filter((m) => m.isAvailable === "available");
  }, [categories, selected]);

  // Total active dessert menus available today, across all dessert categories.
  // Drives the "X/Y today" badge — user can order 1 of each, so Y is the cap.
  const totalDesserts = useMemo<number>(() => {
    return categories.reduce(
      (sum, c) =>
        sum
        + c.menus.filter(
          (m) => m.isAvailable === "available" && m.isActive === "active",
        ).length,
      0,
    );
  }, [categories]);

  const handleSelectMenu = (menuID: number, menuName: string) => {
    if (!open && !bypass) return;
    dispatch({
      type: "SETMENU",
      payload: {
        menu_id: menuID,
        menu_name: menuName,
        type: "",
        sweetness: 100,
        remark: "",
        tumblerImage: "",
      },
    });
    const path = user?.role === "admin" || user?.role === "dev" ? "form-dessert-admin" : "form-dessert";
    navigate(`/${path}/${menuName}/${menuID}`);
  };

  return (
    <div className="min-h-screen bg-white font-sans pb-[96px]">
      <header className="px-5 pt-[24px] pb-[16px]">
        <p className="eyebrow-v2 mb-2">Dessert bar</p>
        <h1 className="font-display font-black text-[28px] leading-[1.15] tracking-tightest text-ink-1000">
          {open ? "Sweet things await 🍰" : "We're closed for now"}
        </h1>
        <div className="flex items-center gap-2 mt-2">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 h-[22px] rounded-full text-[10px] font-bold uppercase tracking-wider ${
              open ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${open ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
            {open ? "Open now" : "Closed"}
          </span>
          {!open && next && (
            <span className="text-xs text-ink-500">
              Opens {moment(now).isSame(next, "day") ? "today" : moment(now).clone().add(1, "day").isSame(next, "day") ? "tomorrow" : next.format("dddd")} at {next.format("HH:mm")}
            </span>
          )}
          {totalDesserts > 0 && (() => {
            // Effective cap: whichever is smaller — the global daily dessert
            // limit, or the count of distinct dessert menus available.
            const cap = Math.min(dessertLimit, totalDesserts);
            const reached = orderedDessertIds.size >= cap;
            return (
              <span
                className={`inline-flex items-center px-2.5 h-[22px] rounded-full text-[11px] font-bold ${
                  reached ? "bg-accent text-accent-on" : "bg-ink-100 text-ink-900"
                }`}
                title="Desserts ordered today / daily limit"
              >
                {orderedDessertIds.size}/{cap} today
              </span>
            );
          })()}
        </div>
      </header>

      {/* Closed hero */}
      {!open && (
        <div className="px-5 pt-[20px]">
          <div className="rounded-3xl bg-hero text-hero-on p-6 relative overflow-hidden">
            <div className="absolute -right-4 -top-4 w-[140px] h-[140px] rounded-full" style={{ background: "var(--festive-1)", opacity: "var(--decor-opacity)" }} />
            <div className="relative">
              <p className="eyebrow-v2 text-hero-muted mb-2">Dessert hours</p>
              <h2 className="font-display font-black text-[22px] leading-tight tracking-tight">
                Treats are served {rows[0]?.time ?? ""}
              </h2>
              <p className="text-sm text-hero-muted mt-1.5">
                {rows[0]?.days ?? "Mon–Fri"} · come by during dessert hours.
              </p>
            </div>
          </div>
          {bypass && (
            <p className="mt-2 text-[11px] text-ink-500">
              You can still preview the menu (dev bypass).
            </p>
          )}
        </div>
      )}

      {/* Tabs */}
      {categories.length > 0 && (
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-ink-100">
          <div className="px-5 overflow-x-auto scrollbar-hide">
            <div className="flex gap-7 pt-4">
              {categories.slice(0, 6).map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelected(cat.id)}
                  className={`whitespace-nowrap ${cat.id === selected ? "tab-v2-active" : "tab-v2"}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Section title */}
      <div className="px-5 pt-[24px] pb-[8px] flex items-end justify-between">
        <h2 className="font-display font-black text-[20px] tracking-tight text-ink-1000">
          {categories.find((c) => c.id === selected)?.name ?? "Dessert"}
        </h2>
        <span className="text-xs text-ink-500 font-medium">{menus.length} items</span>
      </div>

      {/* List */}
      <main className="px-5">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="row-v2 animate-pulse">
              <div className="w-[64px] h-[64px] rounded-2xl bg-ink-100" />
              <div className="flex-1 space-y-2">
                <div className="h-[16px] bg-ink-200 rounded w-2/3" />
                <div className="h-3 bg-ink-100 rounded w-1/2" />
              </div>
              <div className="w-[36px] h-[36px] rounded-full bg-ink-100" />
            </div>
          ))
        ) : categories.length === 0 ? (
          <div className="py-[64px] flex flex-col items-center justify-center text-center">
            <div className="w-[56px] h-[56px] rounded-full bg-ink-100 flex items-center justify-center mb-4 text-2xl">
              🍰
            </div>
            <p className="font-display font-black text-lg text-ink-1000">No dessert menu yet</p>
            <p className="text-sm text-ink-500 mt-1">Ask the admin to add a "Dessert" category</p>
          </div>
        ) : menus.length === 0 ? (
          <div className="py-[64px] flex flex-col items-center justify-center text-center">
            <div className="w-[56px] h-[56px] rounded-full bg-ink-100 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-ink-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="font-display font-black text-lg text-ink-1000">No menu available</p>
            <p className="text-sm text-ink-500 mt-1">Try another category</p>
          </div>
        ) : (
          menus.map((menu) => {
            const hasOwnSchedule =
              !!menu.availableDays || menu.availableFromMin != null || menu.availableToMin != null;
            const inWindow = hasOwnSchedule ? isMenuAvailableNow(menu, now) : true;
            const alreadyOrdered = orderedDessertIds.has(menu.id) && !bypass;
            const stock = stockByMenu[menu.id];
            const soldOut = !!stock && stock.remaining <= 0 && !bypass;
            const dailyDessertReached =
              !bypass && orderedDessertIds.size >= dessertLimit && !alreadyOrdered;
            const disabled =
              menu.isActive !== "active"
              || (!open && !bypass)
              || (!inWindow && !bypass)
              || alreadyOrdered
              || soldOut
              || dailyDessertReached;
            const summary = hasOwnSchedule ? summarizeMenuAvailability(menu) : null;
            return (
              <button
                key={menu.id}
                onClick={() => !disabled && handleSelectMenu(menu.id, menu.name)}
                disabled={disabled}
                className={`row-v2 w-full text-left ${disabled ? "opacity-40" : "active:bg-ink-50"}`}
              >
                <div className="flex-shrink-0 w-[64px] h-[64px] rounded-2xl bg-ink-100 flex items-center justify-center overflow-hidden">
                  {menu.image ? (
                    <img src={ikThumb(menu.image, 200, 200)} alt={menu.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span className="font-display font-black text-2xl text-ink-300">
                      {menu.name.slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-base text-ink-1000 truncate">{menu.name}</span>
                    {alreadyOrdered && (
                      <span className="shrink-0 inline-flex items-center gap-1 px-2 h-[20px] rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-900">
                        ✓ Ordered today
                      </span>
                    )}
                    {stock && !alreadyOrdered && (
                      soldOut ? (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 h-[20px] rounded-full text-[10px] font-bold bg-rose-100 text-rose-900">
                          Sold out
                        </span>
                      ) : (
                        <span className={`shrink-0 inline-flex items-center gap-1 px-2 h-[20px] rounded-full text-[10px] font-bold ${
                          stock.remaining <= 5 ? "bg-amber-100 text-amber-900" : "bg-ink-100 text-ink-700"
                        }`}>
                          {stock.remaining}/{stock.dailyStock} left today
                        </span>
                      )
                    )}
                  </div>
                  {summary && (
                    <div className="text-[11px] text-ink-500 mt-0.5 truncate">
                      {inWindow ? "Available now · " : "Only "}{summary}
                    </div>
                  )}
                  {menu.description && (
                    <div className="text-xs text-ink-500 mt-0.5 line-clamp-1">{menu.description}</div>
                  )}
                </div>
                <div
                  className={`flex-shrink-0 w-[36px] h-[36px] rounded-full flex items-center justify-center ${
                    disabled ? "bg-ink-100 text-ink-300" : "bg-accent text-accent-on"
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    {alreadyOrdered ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    )}
                  </svg>
                </div>
              </button>
            );
          })
        )}
      </main>
    </div>
  );
};

export default MenuDessertList;
